import * as React from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";

import { cn } from "@/lib/utils";

type ViewportWheelHandler = (event: WheelEvent, viewport: HTMLDivElement) => void;

type ScrollAreaProps = React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
  onViewportWheel?: ViewportWheelHandler;
  scrollbarClassName?: string;
  scrollbarOrientation?: "vertical" | "horizontal" | "both" | "none";
  viewportClassName?: string;
};

const ScrollArea = React.forwardRef<React.ElementRef<typeof ScrollAreaPrimitive.Root>, ScrollAreaProps>(
  (
    {
      className,
      children,
      onViewportWheel,
      scrollbarClassName,
      scrollbarOrientation = "vertical",
      viewportClassName,
      ...props
    },
    ref,
  ) => {
    const viewportRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
      const viewport = viewportRef.current;
      if (!viewport || !onViewportWheel) return undefined;
      const handleWheel = (event: WheelEvent) => onViewportWheel(event, viewport);
      viewport.addEventListener("wheel", handleWheel, { passive: false });
      return () => viewport.removeEventListener("wheel", handleWheel);
    }, [onViewportWheel]);

    return (
      <ScrollAreaPrimitive.Root ref={ref} className={cn("relative overflow-hidden", className)} {...props}>
        <ScrollAreaPrimitive.Viewport
          ref={viewportRef}
          className={cn("h-full w-full rounded-[inherit]", viewportClassName)}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        {scrollbarOrientation === "vertical" || scrollbarOrientation === "both" ? (
          <ScrollBar orientation="vertical" className={scrollbarClassName} />
        ) : null}
        {scrollbarOrientation === "horizontal" || scrollbarOrientation === "both" ? (
          <ScrollBar orientation="horizontal" className={scrollbarClassName} />
        ) : null}
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    );
  },
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
